/**
 * 文件上传和解析服务
 * 
 * 提供统一的文件处理方法，可被以下组件复用：
 * - DropZoneOverlay（拖拽上传）
 * - DocxParser（文档解析）
 * - AudioParser（音频上传转录）
 */

import mammoth from 'mammoth';
import { getOSSClient } from '@/utils/ossClient';
import { processAudioV2 } from '@/services/api';
import { sendMessage } from '@/utils/slsClient';
import { handleError, handleSuccess } from '@/utils/helpers';
import { ERROR_MESSAGES, FILE_CONSTANTS, STATUS_MESSAGES, SUCCESS_MESSAGES } from '@/constants/messages';

// 音频预处理模块（单声道转换）
// 如果不需要此功能，删除 audio-preprocess.ts 并注释掉下面的导入和导出
import {
  preprocessAudio,
  type AudioPreprocessConfig,
  type AudioInfo,
  updateAudioPreprocessConfig,
  resetAudioPreprocessConfig,
  getAudioInfo,
  convertToMono,
  audioPreprocessConfig,
  DEFAULT_AUDIO_PREPROCESS_CONFIG,
} from './audio-preprocess';

// 重新导出音频预处理相关函数，保持对外接口不变
export {
  preprocessAudio,
  type AudioPreprocessConfig,
  type AudioInfo,
  updateAudioPreprocessConfig,
  resetAudioPreprocessConfig,
  getAudioInfo,
  convertToMono,
  audioPreprocessConfig,
  DEFAULT_AUDIO_PREPROCESS_CONFIG,
};

// ============= 类型定义 =============

/** 文档解析结果 */
export interface DocxParseResult {
  fileName: string;
  content: string;
}

/** 音频上传进度回调参数 */
export interface AudioUploadProgress {
  /** 上传进度百分比 0-100 */
  percent: number;
  /** 当前状态 */
  status: 'uploading' | 'transcribing' | 'done' | 'error';
  /** 状态消息 */
  message: string;
}

/** 音频处理结果 */
export interface AudioProcessResult {
  fileName: string;
  /** 转录后的文本内容 */
  transcription: string;
}

/** 音频处理选项 */
export interface AudioProcessOptions {
  /** 进度回调 */
  onProgress?: (progress: AudioUploadProgress) => void;
  /** 状态变化通知（兼容 setA2tNotice） */
  onStatusChange?: (status: string) => void;
  /** 音频预处理配置（可覆盖全局配置） */
  preprocessConfig?: Partial<AudioPreprocessConfig>;
}

// ============= 文档处理 =============

/**
 * 解析 docx 文件内容
 * 
 * @param file - 要解析的文件（File 或 Blob）
 * @param fileName - 文件名（用于结果标识）
 * @returns Promise<DocxParseResult> - 解析结果
 * 
 * @example
 * ```ts
 * const result = await parseDocxFile(file, file.name);
 * console.log(result.content); // HTML 格式的文档内容
 * ```
 */
export async function parseDocxFile(
  file: File | Blob,
  fileName: string
): Promise<DocxParseResult> {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          
          if (!arrayBuffer) {
            throw new Error('Failed to read file');
          }
          
          // 使用 mammoth 解析 docx 内容
          const result = await mammoth.convertToHtml({ arrayBuffer });
          
          resolve({
            fileName,
            content: result.value,
          });
        } catch (error) {
          handleError(error, ERROR_MESSAGES.FILE_PARSE_ERROR);
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        handleError(error, ERROR_MESSAGES.FILE_PARSE_ERROR);
        reject(error);
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      handleError(error, ERROR_MESSAGES.FILE_PARSE_ERROR);
      reject(error);
    }
  });
}

/**
 * 批量解析多个 docx 文件
 * 
 * @param files - 文件数组
 * @returns Promise<Record<string, string>> - 文件名到内容的映射
 */
export async function parseDocxFiles(
  files: File[]
): Promise<Record<string, string>> {
  const results: Record<string, string> = {};
  
  await Promise.all(
    files.map(async (file) => {
      const result = await parseDocxFile(file, file.name);
      results[result.fileName] = result.content;
    })
  );
  
  return results;
}

// ============= 音频处理 =============

/**
 * 上传音频文件到 OSS 并调用转录服务
 * 
 * @param file - 要上传的音频文件
 * @param options - 处理选项
 * @returns Promise<AudioProcessResult> - 处理结果
 * 
 * @example
 * ```ts
 * const result = await uploadAndTranscribeAudio(file, {
 *   onProgress: (progress) => {
 *     console.log(`${progress.status}: ${progress.percent}%`);
 *   },
 *   onStatusChange: (status) => {
 *     setNotice(status);
 *   }
 * });
 * console.log(result.transcription);
 * ```
 */
export async function uploadAndTranscribeAudio(
  file: File,
  options: AudioProcessOptions = {}
): Promise<AudioProcessResult> {
  const { onProgress, onStatusChange, preprocessConfig } = options;
  
  // 保存原始文件名用于显示
  const oriName = file.name;
  
  // 辅助函数：更新进度
  const updateProgress = (
    percent: number,
    status: AudioUploadProgress['status'],
    message: string
  ) => {
    onProgress?.({ percent, status, message });
  };
  
  try {
    // 1. 音频预处理（声道转换）
    onStatusChange?.('音频预处理中...');
    console.log('[音频上传] 开始预处理，enableMonoConversion:', preprocessConfig?.enableMonoConversion ?? audioPreprocessConfig.enableMonoConversion);
    const preprocessResult = await preprocessAudio(file, preprocessConfig);
    const processedFile = preprocessResult.file;
    console.log('[音频上传] 预处理完成，wasConverted:', preprocessResult.wasConverted, '文件名:', processedFile.name, '大小:', processedFile.size);
    
    if (preprocessResult.wasConverted) {
      console.log(`[音频上传] 已将 ${preprocessResult.originalInfo?.numberOfChannels} 声道转换为单声道`);
    }
    
    // 使用处理后的文件名和路径
    const uploadFileName = processedFile.name;
    const encodedName = encodeURIComponent(uploadFileName);
    const filePathWithName = `audio/${uploadFileName}`;
    
    // 2. 获取 OSS 客户端
    console.log('[音频上传] 正在获取 OSS 客户端...');
    const client = await getOSSClient();
    console.log('[音频上传] OSS 客户端获取成功');
    
    // 3. 通知开始上传
    sendMessage('音频', oriName, '准备上传');
    onStatusChange?.(STATUS_MESSAGES.UPLOADING);
    updateProgress(0, 'uploading', STATUS_MESSAGES.UPLOADING);
    
    // 4. 上传到 OSS（使用处理后的文件）
    console.log('[音频上传] 开始上传到 OSS，路径:', filePathWithName);
    await client.multipartUpload(filePathWithName, processedFile, {
      progress: (percentage: number) => {
        const percent = Math.round(percentage * 100);
        updateProgress(percent, 'uploading', STATUS_MESSAGES.UPLOADING);
      },
      partSize: FILE_CONSTANTS.PART_SIZE,
      meta: {
        originalName: encodedName,
        type: processedFile.type,
      },
    });
    
    // 5. 开始转录
    console.log('[音频上传] OSS 上传完成，开始转录');
    sendMessage('音频', oriName, '准备转录');
    onStatusChange?.(STATUS_MESSAGES.TRANSCRIBING);
    updateProgress(100, 'transcribing', STATUS_MESSAGES.TRANSCRIBING);
    
    // 6. 调用转录 API（使用 V2 接口，返回句子列表）
    const response = await processAudioV2(filePathWithName);
    
    if (response.data.success) {
      // 转录成功，将句子列表转换为文本
      onStatusChange?.(STATUS_MESSAGES.TRANSCRIPTION_COMPLETE);
      updateProgress(100, 'done', STATUS_MESSAGES.TRANSCRIPTION_COMPLETE);
      handleSuccess(SUCCESS_MESSAGES.TRANSCRIPTION_SUCCESS);
      
      // 将句子列表转换为 Markdown 格式文本（包含时间信息）
      const formatTime = (ms: number) => {
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        
        if (hours > 0) {
          return `${hours}小时${minutes}分${seconds}秒`;
        }
        return `${minutes}分${seconds}秒`;
      };
      
      const transcriptionText = response.data.data
        .map((sentence: any) => {
          const timeStamp = formatTime(sentence.beginTime);
          return `**${sentence.speakerName} - ${timeStamp}**\n\n${sentence.text}`;
        })
        .join('\n\n');

      
      return {
        fileName: oriName,
        transcription: transcriptionText,
      };
    } else {
      // 转录失败
      const errorMessage = response.data.message || ERROR_MESSAGES.TRANSCRIPTION_ERROR;
      onStatusChange?.(STATUS_MESSAGES.TRANSCRIPTION_ERROR);
      updateProgress(0, 'error', STATUS_MESSAGES.TRANSCRIPTION_ERROR);
      handleError(new Error(errorMessage), ERROR_MESSAGES.TRANSCRIPTION_ERROR);
      
      throw new Error(errorMessage);
    }
  } catch (error: any) {
    // 处理错误
    console.error('[音频上传] 流程出错，错误详情:', error?.message, error?.name, error);
    sendMessage('音频', '处理出错' + oriName, error.message);
    onStatusChange?.(STATUS_MESSAGES.TRANSCRIPTION_ERROR);
    updateProgress(0, 'error', error.message);
    handleError(error, `${oriName} ${ERROR_MESSAGES.UPLOAD_ERROR}: ${error.message}`);
    
    throw error;
  }
}

// ============= 文件验证 =============

/** 支持的文档格式 */
export const SUPPORTED_DOCUMENT_EXTENSIONS = ['.docx'];

/** 支持的音频格式 */
export const SUPPORTED_AUDIO_EXTENSIONS = ['.wav', '.m4a', '.aac', '.mp3', '.ogg'];

/** 音频文件最大大小 (2GB) */
export const MAX_AUDIO_FILE_SIZE = 2 * 1024 * 1024 * 1024;

/**
 * 验证是否为支持的文档文件
 */
export function isValidDocumentFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return SUPPORTED_DOCUMENT_EXTENSIONS.some(ext => name.endsWith(ext));
}

/**
 * 验证是否为支持的音频文件
 */
export function isValidAudioFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return SUPPORTED_AUDIO_EXTENSIONS.some(ext => name.endsWith(ext));
}

/**
 * 验证音频文件大小是否在限制内
 */
export function isAudioFileSizeValid(file: File): boolean {
  return file.size <= MAX_AUDIO_FILE_SIZE;
}

/**
 * 获取文件验证错误信息
 */
export function getFileValidationError(file: File, type: 'document' | 'audio'): string | null {
  if (type === 'document') {
    if (!isValidDocumentFile(file)) {
      return `文件 ${file.name} 格式不支持，仅支持 ${SUPPORTED_DOCUMENT_EXTENSIONS.join(', ')} 格式`;
    }
  } else if (type === 'audio') {
    if (!isValidAudioFile(file)) {
      return `文件 ${file.name} 格式不支持，仅支持 ${SUPPORTED_AUDIO_EXTENSIONS.join(' / ')} 格式`;
    }
    if (!isAudioFileSizeValid(file)) {
      return '文件大小不能超过 2GB';
    }
  }
  return null;
}
